import React, { useRef, useState, useEffect, useContext } from "react";
import styles from "../../../../styles/components/Modal/Employees/Modal/FilterModal.module.css";
import { InputSelector } from "../../../UI/InputSelector";
import CloseIcon from "@mui/icons-material/Close";
import { DropdownSelector } from "../../../UI/DropdownSelector";
import { filterFieldsConfig } from "../../../../utils/api/Admin/desk/filterFieldsConfig";
import { LangContext } from "../../../../context/LangContext";
import { ThemeContext } from "../../../../context/ThemeContext";
import { saveWithTTL } from "../../../../utils/custom/localStorageTTL";

const BASE_EMPLOYEE_STORAGE_KEY = 'employeeFiltersState';

const translations = {
    ru: {
        title: "Фильтры",
        searchLabel: "Email или Имя",
        statusLabel: "Статус",
        statusOptions: {
            all: 'Все',
            active: 'Активный',
            blocked: 'Заблокирован'
        },
        reset: "Сбросить",
        confirm: "Подтвердить"
    },
    en: {
        title: "Filters",
        searchLabel: "Email or Name",
        statusLabel: "Status",
        statusOptions: {
            all: 'All',
            active: 'Active',
            blocked: 'Blocked'
        },
        reset: "Reset",
        confirm: "Confirm"
    },

};

export const FilterModal = ({ handleneOpenModal, setFilters, currentFilters, onApplyFilters, role }) => {
    const { langSelected } = useContext(LangContext);
    const { theme } = useContext(ThemeContext);
    const isDark = theme === 'dark';
    const t = translations[langSelected] || translations.ru;

    const modalRef = useRef(null);
    const [focusedField, setFocusedField] = useState(null);
    const [touchedFields, setTouchedFields] = useState({});
    const [errors, setErrors] = useState({});
    const [internalFilters, setInternalFilters] = useState(currentFilters || {});

    const EMPLOYEE_STORAGE_KEY_WITH_ROLE = `${BASE_EMPLOYEE_STORAGE_KEY}_${role}`;

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (modalRef.current && !modalRef.current.contains(event.target)) {
                onApplyFilters(internalFilters);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [onApplyFilters, modalRef, internalFilters]);

    const handleFilterChange = (filterName, value) => {
        setInternalFilters((prevFilters) => ({
            ...prevFilters,
            [filterName]: value,
        }));
    };

    const handleApplyFilters = () => {
        onApplyFilters(internalFilters);
    };

    const handleClearFilters = () => {
        const clearedFilters = {};
        setInternalFilters(clearedFilters);
        onApplyFilters(clearedFilters);
        saveWithTTL(EMPLOYEE_STORAGE_KEY_WITH_ROLE, {});
    };

    const handleCloseClick = () => {
        onApplyFilters(internalFilters);
    };

    const handleInputFocus = (fieldName) => {
        setFocusedField(fieldName);
    };

    const handleInputBlur = (fieldName) => {
        setFocusedField(null);
        setTouchedFields((prevTouched) => ({ ...prevTouched, [fieldName]: true }));
    };
    

    const translateOptions = (options) => {
        return options.map(option => ({
            ...option,
            label: t.statusOptions[option.labelKey]
        }));
    };

    return (
        <div className={`${styles.modal} ${isDark ? styles.darkModal : ''}`}>
            <div className={`${styles.filtersContainer} ${isDark ? styles.darkFiltersContainer : ''}`} ref={modalRef}>
                <div className={`${styles.modalLabel} ${isDark ? styles.darkModalLabel : ''}`}>
                    <div>
                        <p className={`${isDark ? styles.darkModalLabelText : ''}`}>{t.title}</p>
                    </div>
                    <div>
                        <div onClick={handleCloseClick} className={`${isDark ? styles.darkModalLabelCloseButton : ''}`}>
                            <CloseIcon />
                        </div>
                    </div>
                </div>
                <div className={styles.modalContent}>
                    <div className={styles.section}>
                        {filterFieldsConfig.map((field) => (
                            <div key={field.key} className={styles.filterItem}>
                                <div className={styles.itemLabel}>
                                    <span className={`${styles.itemLabelValue} ${isDark ? styles.darkItemLabelValue : ''}`}>{t[field.labelKey]}:</span>
                                </div>
                                {field.type === "input" && (
                                    <InputSelector
                                        filterName={field.key}
                                        selectedValue={internalFilters[field.key] || ''}
                                        handleFilterChange={handleFilterChange}
                                        type={field.type}
                                        isFocused={focusedField === field.key}
                                        onFocus={handleInputFocus}
                                        onBlur={handleInputBlur}
                                        hasError={!!errors[field.key] && touchedFields[field.key]}
                                        errorMessage={errors[field.key]}
                                        isTouched={touchedFields[field.key]}
                                        isDark={isDark}
                                    />
                                )}
                                {field.type === "dropdown" && (
                                    <DropdownSelector
                                        filterName={field.key}
                                        selectedValue={internalFilters[field.key] || ''}
                                        handleFilterChange={handleFilterChange}
                                        options={translateOptions(field.options)}
                                        isFocused={focusedField === field.key}
                                        onFocus={handleInputFocus}
                                        onBlur={handleInputBlur}
                                        hasError={!!errors[field.key] && touchedFields[field.key]}
                                        errorMessage={errors[field.key]}
                                        isTouched={touchedFields[field.key]}
                                        isDark={isDark}
                                    />
                                )}
                            </div>
                        ))}
                    </div>
                </div>
                <div className={`${styles.modalFooter} ${isDark ? styles.darkModalFooter : ''}`}>
                    <div className={`${styles.resetWrapper} ${isDark ? styles.darkResetWrapper : ''}`}>
                        <button className={`${styles.clearButton} ${isDark ? styles.darkClearButton : ''}`} onClick={handleClearFilters}>
                            {t.reset}
                        </button>
                    </div>
                    <div className={`${styles.confirmationWrapper} ${isDark ? styles.darkConfirmationWrapper : ''}`}>
                        <button className={`${styles.submitButton} ${isDark ? styles.darkSubmitButton : ''}`} onClick={handleApplyFilters}>
                            {t.confirm}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};